import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import MealPlanFields from 'components/meal-plan/MealPlanFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultMealPlanFormValidations from 'common/validations/form/defaultMealPlan';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { createMealPlanInitialValues } from 'common/form/initial-values/create';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import { handleClientIdChange } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import mealPlanSlice from 'store/modules/mealPlan';

const requiredFields = ['created_at', 'pdf_document', 'user_id'];

function MealPlanCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('mealPlan.create', { ns: 'routes' });
  const formRef = useRef(null);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const clientOptions = useSelector((state) => state.client.data.search);
  const searchStatus = useSelector((state) => state.client.status.search);
  const status = useSelector((state) => state.mealPlan.status.general);

  useEffect(() => {
    if (status === requestStatus.success && formRef.current) {
      formRef.current.restart();
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onCreate = useCallback(
    (values) => {
      const dataToSave = getDataToSave(values, 'mealPlan');
      dispatch(mealPlanSlice.actions.create(dataToSave));
    },
    [dispatch]
  );

  const onClientIdChange = handleClientIdChange(dispatch);

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createMealPlanInitialValues}
        onSubmit={onCreate}
        validate={defaultMealPlanFormValidations}
        render={({ handleSubmit, form, invalid, pristine }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <MealPlanFields
                clientOptions={clientOptions}
                onClientIdChange={onClientIdChange}
                requiredFields={requiredFields}
                searchStatus={searchStatus}
              />
              <StyledButtonsContainer>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listMealPlan.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('mealPlan.create', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </form>
          );
        }}
      />
    </>
  );
}

export default MealPlanCreate;
